import React, { useEffect, useState } from "react";
import styled, { useTheme } from "styled-components";
import { useTranslate } from '@tolgee/react';

import { useAuthService } from "../../Context/AuthContext";
import { getFlag } from "../../Helpers/FlagHandler";
import { colors } from "../../Assets/styles/colors";
import PrimaryButton from "../GenericComponents/PrimaryButton";

import { ReactComponent as Arrow } from "../../Assets/iconsv2/generic/forwardArrow.svg";
import LanguageListSelector from "../Auth Components/LanguageListSelector";
import { useNotify } from "../../Helpers/Notifications";
import { ReactComponent as Check } from "../../Assets/iconsv2/generic/check.svg";
import { ReactComponent as CheckEmpty } from "../../Assets/iconsv2/generic/checkEmpty.svg";
import { useGenericComponentsStateService } from "../../Context/GenericComponentsStateContext";
import LocalStorage from "../../Helpers/LocalStorage";

const ChatsSettings = () => {
  const { notifySuccess, notifyError } = useNotify();
  const { user, updateUser, translationsLanguages } = useAuthService();
  const { automaticTranslations, setAutomaticTranslations } =
    useGenericComponentsStateService();
  const theme = useTheme();
  const [selectedLanguage, setSelectedLanguage] = useState(user.language);
  const [infoChanged, setInfoChanged] = useState(false);
  const [isLanguageSelectorOpen, setIsLanguageSelectorOpen] = useState(false);
  const { t } = useTranslate();
  const checkInfoChanged = () => {
    if (user.language !== selectedLanguage) {
      setInfoChanged(true);
    } else {
      setInfoChanged(false);
    }
  };

  const handleUpdateUser = () => {
    var objectData = {};
    if (selectedLanguage !== user.language) {
      objectData.language = selectedLanguage;
    }
    updateUser(
      objectData,
      function (response) {
        notifySuccess(`${t('profile.profile_updated')}`);
        setInfoChanged(false);
      },
      function (error) {
        console.log(error, "error");
        notifyError(`${t('general.something_wrong')}`);
      }
    );
  };
  const displaySlectedLanguageFromList = (language) => {
    let found = translationsLanguages.find((item) => item.name === language);
    return found;
  };

  const openLanguageSelector = () => {
    setIsLanguageSelectorOpen(!isLanguageSelectorOpen);
  };

  const handleAutomaticTranslateChange = (automaticTranslate) => {
    setAutomaticTranslations(automaticTranslate);
    LocalStorage.setAutomaticTranslate(automaticTranslate);
  };

  useEffect(() => {
    checkInfoChanged();
  }, [selectedLanguage]);

  useEffect(() => {
    setSelectedLanguage(user.language);
  }, [user]);

  return (
    <Container>
      {isLanguageSelectorOpen && (
        <LanguageListSelector
          isLanguageSelectorOpen={isLanguageSelectorOpen}
          openLanguageList={openLanguageSelector}
          valuesList={translationsLanguages}
          setValue={setSelectedLanguage}
          selectedValue={selectedLanguage}
        />
      )}
      <Header>
        <HeaderTitle>{t('general.chats')}</HeaderTitle>
        {infoChanged && (
          <PrimaryButton
            onClick={() => handleUpdateUser()}
            text={t('general.save')}
          />
        )}
      </Header>

      <ContentWrapper>
        <Content>
          <Group>
            <GroupTitle>{t('settings.language').toUpperCase()}</GroupTitle>
            <GroupDescription>
              {t('settings.chat_settings_language_text')}
            </GroupDescription>
            <GroupContentWrapper>
              <OptionWithPicker>
                <OptionWithPickerText>
                  {t('profile.language')}
                </OptionWithPickerText>
                <LanguageItem onClick={() => openLanguageSelector()}>
                  <Flag
                    src={getFlag(
                      displaySlectedLanguageFromList(selectedLanguage)
                        .country_code
                    )}
                    alt={selectedLanguage}
                  />{" "}
                  {displaySlectedLanguageFromList(selectedLanguage).name} (
                  {displaySlectedLanguageFromList(selectedLanguage).translation}
                  )
                  <Arrow style={{ marginLeft: "auto" }} />
                </LanguageItem>
              </OptionWithPicker>
            </GroupContentWrapper>
          </Group>
          <Group>
            <GroupTitle style={{ textTransform: "uppercase" }}>
              {t('settings.automatic_translations')}
            </GroupTitle>
            <GroupDescription>
              {t('settings.automatic_translations_text')}
            </GroupDescription>

            <ToggleContainer>
              <ToggleButton
                selected={automaticTranslations === true}
                onClick={(e) => handleAutomaticTranslateChange(true)}
              >
                {t('general.enabled')}
                {automaticTranslations === true ? (
                  <CheckedWrapper>
                    <Check />
                  </CheckedWrapper>
                ) : (
                  <CheckEmptyWrapper>
                    <CheckEmpty />
                  </CheckEmptyWrapper>
                )}
              </ToggleButton>
              <ToggleButton
                selected={automaticTranslations === false}
                onClick={(e) => handleAutomaticTranslateChange(false)}
              >
                {t('general.disabled')}
                {automaticTranslations === false ? (
                  <CheckedWrapper>
                    <Check />
                  </CheckedWrapper>
                ) : (
                  <CheckEmptyWrapper>
                    <CheckEmpty />
                  </CheckEmptyWrapper>
                )}
              </ToggleButton>
            </ToggleContainer>
          </Group>
        </Content>
      </ContentWrapper>
    </Container>
  );
};

export default ChatsSettings;
const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  overflow: hidden;
`;
const Header = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 19px 16px;
  align-items: center;
  background: ${({ theme }) => theme.menu.background};
  border-bottom: 1px solid ${({ theme }) => theme.sidebar.borderColor};
  gap: 20px;
`;
const HeaderTitle = styled.h3`
  font-weight: 700;
  font-size: 20px;
  line-height: 32px;
  letter-spacing: 0.04px;
  color: ${({ theme }) => theme.menu.primaryTextColor};
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 30px;
  background: ${({ theme }) => theme.menu.background};
  height: 100%;
  overflow: auto;
`;
const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
  margin: 0 auto;
  max-width: 631px;
  width: 100%;
  overflow: hidden;
`;

const Group = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const GroupContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.menu.groupContentBackground};
  border-radius: 4px;
  overflow: hidden;
  padding: 8px 16px;
`;

const Flag = styled.img`
  width: 25px;
  height: 25px;
  border-radius: 100px;
  object-fit: cover;
`;

const GroupTitle = styled.p`
  padding-top: 10px;
  font-size: 14px;
  font-weight: 600;
  line-height: 16px;
  letter-spacing: 0.04px;
  vertical-align: middle;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
`;
const GroupDescription = styled.p`
  font-weight: 400;
  font-size: 14px;
  line-height: 22px;
  letter-spacing: 0.04px;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
`;
const OptionWithPicker = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  gap: 10px;
  padding: 8px 0;
`;
const OptionWithPickerText = styled.p`
  min-width: 100px;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  font-weight: 500;
  font-size: 16px;
  line-height: 22px;
  letter-spacing: -0.41px;
`;
const LanguageItem = styled.h3`
  display: flex;
  align-items: center;
  width: 100%;
  color: ${colors.listColor};
  color: ${({ selected, theme }) =>
    selected ? theme.menu.selectedTextColor : theme.menu.primaryTextColor};
  background: ${({ selected, theme }) =>
    selected
      ? theme.menu.selectedOpionBackground
      : theme.menu.groupContentBackground};
  font-size: 16px;
  line-height: 22px;
  font-weight: 500;
  cursor: pointer;
  gap: 10px;
  border-bottom: 1px solid ${({ theme }) => theme.sidebar.borderColor};
  &:last-child {
    border: none;
  }
`;

const ToggleButton = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  border: none;
  background: ${({ theme, selected }) =>
    selected ? theme.primaryButton.background : "none"};
  font-weight: 500;
  font-size: 16px;
  line-height: 20px;
  color: ${({ theme, selected }) =>
    selected ? theme.primaryButton.color : theme.menu.secondaryTextColor};
  padding: 14px 16px;
  width: 100%;
  justify-content: center;
  min-width: 120px;
  cursor: pointer;
  svg {
    margin-left: auto;
    height: 20px;
    fill: transparent;
    color: transparent;
  }
`;
const ToggleContainer = styled.div`
  display: flex;
  align-items: center;
  border-radius: 4px;
  overflow: hidden;
  background: ${({ theme }) => theme.menu.groupContentBackground};
  width: 100%;
`;

const CheckedWrapper = styled.div`
  display: flex;
  svg {
    color: ${({ theme }) => theme.check.background};
    fill: ${({ theme }) => theme.check.checkSign};
  }
`;
const CheckEmptyWrapper = styled.div`
  display: flex;
  svg {
    color: ${({ theme }) => theme.check.border};
    stroke: ${({ theme }) => theme.check.checkSign};
  }
`;
